import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SCROLL_DOWN, SCROLL_UP } from '../constants';
import { useBoolean } from '../hooks';
import { buildInitialSettings, settingsReducer } from '../state/settings/businessSettingsReducer';

/**
 * @type {React.Context<BusinessAppState>}
 */
const BusinessAppStateContext = React.createContext({});

/**
 * @typedef {Object} BusinessAppStateSettings
 * @property {boolean} showContactButton
 */

/**
 * @typedef {Object} BusinessAppState
 * @property {BusinessAppStateSettings} settings
 * @property {({ name: string, value: any }) => void} dispatchSetting
 * @property {boolean} contactFormIsOpen
 * @property {() => void} toggleContactForm
 * @property {boolean} hamburgerMenuIsOpen
 * @property {() => void} closeHamburgerMenu
 * @property {() => void} toggleHamburgerMenu
 * @property {*} footerBlok
 * @property {(value: any) => void} setFooterBlok
 * @property {number} headerHeight
 * @property {(number) => void} setHeaderHeight
 */

/**
 * @returns {BusinessAppState}
 */
export const useBusinessAppState = () => {
  /**
   * @type {BusinessAppState}
   */
  const context = useContext(BusinessAppStateContext);
  if (!context) {
    throw new Error('useBusinessAppState must be used within the BusinessAppStateProvider');
  }
  return context;
};

const BusinessAppStateProvider = ({ children, blok }) => {
  const [settings, dispatchSetting] = useReducer(settingsReducer, blok, buildInitialSettings);
  const [footerBlok, setFooterBlok] = useState(null);
  const { value: contactFormIsOpen, setFalse: hideContactForm, toggle: toggleContactForm } = useBoolean(false);
  const { value: hamburgerMenuIsOpen, setFalse: closeHamburgerMenu, toggle: toggleHamburgerMenu } = useBoolean(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  const contextValue = useMemo(
    () => ({
      settings,
      dispatchSetting,
      contactFormIsOpen,
      toggleContactForm,
      hamburgerMenuIsOpen,
      closeHamburgerMenu,
      toggleHamburgerMenu,
      footerBlok,
      setFooterBlok,
      headerHeight,
      setHeaderHeight,
    }),
    [
      settings,
      contactFormIsOpen,
      toggleContactForm,
      hamburgerMenuIsOpen,
      closeHamburgerMenu,
      toggleHamburgerMenu,
      footerBlok,
      headerHeight,
    ],
  );

  useEffect(() => {
    if (contactFormIsOpen) {
      closeHamburgerMenu();
    }
  }, [contactFormIsOpen, closeHamburgerMenu]);

  useEffect(() => {
    if (hamburgerMenuIsOpen) {
      hideContactForm();
    }
  }, [hamburgerMenuIsOpen, hideContactForm]);

  useEffect(() => {
    if (!settings.showContactButton) {
      hideContactForm();
    }
  }, [settings.showContactButton, hideContactForm]);

  const lastScrollRef = useRef(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    const handleDynamicHeader = () => {
      const { body } = document;
      const currentScroll = window.scrollY;
      if (currentScroll <= 0) {
        body.classList.remove(SCROLL_UP);
        return;
      }
      if (currentScroll > lastScrollRef.current && !body.classList.contains(SCROLL_DOWN)) {
        // down
        body.classList.remove(SCROLL_UP);
        body.classList.add(SCROLL_DOWN);
      } else if (currentScroll < lastScrollRef.current && body.classList.contains(SCROLL_DOWN)) {
        // up
        body.classList.remove(SCROLL_DOWN);
        body.classList.add(SCROLL_UP);
      }
      lastScrollRef.current = currentScroll;
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        handleDynamicHeader();
        tickingRef.current = false;
      });
    };

    handleDynamicHeader();

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return <BusinessAppStateContext.Provider value={contextValue}>{children}</BusinessAppStateContext.Provider>;
};

BusinessAppStateProvider.propTypes = {
  children: PropTypes.any,
  blok: PropTypes.object,
};

BusinessAppStateProvider.defaultProps = {
  blok: {},
};

export default BusinessAppStateProvider;
